/* ==========================================================================
   HESTORÉ — shared behaviour  (README-SHOPIFY-MIGRATION.md §3 foundation)
   Port of prototype/js/revamp.js.

   Behaviours:
     1  is-ready      — releases the hero entrance transitions
     1b .is-solid     — header goes solid white once the page scrolls
     1c promo rotator — announcement-bar messages, 4.2s, prev/next arrows
     2  reveal        — IntersectionObserver adds `.in` to
                        [data-reveal] / .rv / [data-clip], with a 1200ms
                        force-reveal safety net
     3  counters      — animates [data-count] numerals into view
     4  read more     — reveals a Read more / Read less control ONLY when
                        `-webkit-line-clamp` has actually truncated the copy
     5  rails         — auto-advance, seamless looping and prev/next controls
                        for the horizontal scroll-snap rails

   Contract with the theme:
     · Everything hangs off `window.HST`; nothing else is added to globals.
     · Every element lookup is guarded — a page that has no header / no promo
       bar / no counters must not throw.  (The original main.js slice queried
       #hdr and #sticky unguarded, threw, and killed every behaviour after it.)
     · Safe to load with `defer`: boot runs on DOMContentLoaded or immediately
       if the document is already parsed.
     · Re-inits on `shopify:section:load` — REQUIRED, or a section re-rendered
       in the theme editor comes back with `[data-clip]` still clipped to
       nothing and `[data-reveal]` still at opacity:0, i.e. invisible.
     · Idempotent: every element is stamped with a `data-hst-*` flag, so a
       repeated init never double-binds a listener or starts a second timer.
     · `prefers-reduced-motion: reduce` is honoured everywhere — reveals and
       counters jump to their end state, the promo bar stops auto-cycling
       (its arrows keep working).

   All work is scoped to `.hst` subtrees so theme chrome is never touched.
   ========================================================================== */
window.HST = window.HST || {};

(function (HST) {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  var reduce = false;
  try {
    reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* matchMedia missing — treat as "motion allowed" */ }

  var supportsIO = 'IntersectionObserver' in window;

  /* Collect `sel` matches inside `root`, INCLUDING root itself when it
     matches.  `shopify:section:load` hands us the section wrapper, so the
     interesting elements are often the root or its direct children rather
     than deep descendants. */
  function find(root, sel) {
    var out = [];
    if (!root) return out;
    try {
      if (root.nodeType === 1 && root.matches && root.matches(sel)) out.push(root);
      var list = root.querySelectorAll(sel);
      for (var i = 0; i < list.length; i++) out.push(list[i]);
    } catch (e) { /* bad selector / detached node */ }
    return out;
  }

  /* Only ever act on our own markup. */
  function inScope(el) {
    return !!(el && el.closest && el.closest('.hst'));
  }

  function scoped(root, sel) {
    var all = find(root, sel), out = [], i;
    for (i = 0; i < all.length; i++) if (inScope(all[i])) out.push(all[i]);
    return out;
  }

  /* One-shot stamp so re-init is a no-op for already-wired elements. */
  function claim(el, flag) {
    if (!el || el.getAttribute(flag) === '1') return false;
    el.setAttribute(flag, '1');
    return true;
  }


  /* ------------------------------------------------- 1 — hero entrance gate
     Adds `is-ready` to <body>; the CSS keys every hero entrance transition
     off it.  Runs once per page — a section reload in the theme editor finds
     the class already present. */

  var readyDone = false;
  function markReady() {
    if (readyDone || !document.body) return;
    readyDone = true;
    document.body.classList.add('is-ready');
  }
  HST.ready = markReady;

  function bootReady() {
    if (document.readyState === 'complete') markReady();
    else window.addEventListener('load', markReady, { once: true });
    /* Never wait on the hero <video> to finish buffering — without this the
       hero copy can sit at opacity:0 for seconds on a slow connection. */
    window.setTimeout(markReady, 900);
  }


  /* ------------------------------------- 1b — header solid-on-scroll toggle
     Transparent over the video hero, solid white once scrolled past 40px.
     A single shared scroll listener drives every registered header, so
     re-init cannot stack listeners. */

  var headers = [];
  var scrollBound = false;

  function syncHeaders() {
    var y = window.scrollY || window.pageYOffset || 0;
    for (var i = headers.length - 1; i >= 0; i--) {
      var h = headers[i];
      if (!h.isConnected) { headers.splice(i, 1); continue; }  /* editor removed it */
      h.classList.toggle('is-solid', y > 40);
    }
  }
  HST.syncHeaders = syncHeaders;

  function initHeader(root) {
    var found = scoped(root, '.header');
    for (var i = 0; i < found.length; i++) {
      if (!claim(found[i], 'data-hst-header')) continue;
      headers.push(found[i]);
    }
    if (!headers.length) return;
    if (!scrollBound) {
      scrollBound = true;
      window.addEventListener('scroll', syncHeaders, { passive: true });
    }
    syncHeaders();
  }


  /* --------------------------------------------- 1c — announcement rotator
     State lives on the `.promo` element itself (`_hstIdx`, `_hstTimer`) so
     several bars, or a bar replaced by the editor, never share a timer. */

  var PROMO_MS = 4200;

  function showPromo(bar, n) {
    var items = bar._hstItems;
    if (!items || !items.length) return;
    bar._hstIdx = (n + items.length) % items.length;
    for (var i = 0; i < items.length; i++) {
      items[i].classList.toggle('is-on', i === bar._hstIdx);
    }
  }

  function autoPromo(bar) {
    window.clearInterval(bar._hstTimer);
    /* Reduced-motion users get the messages without an unrequested
       auto-cycle; the arrows still work. */
    if (reduce) return;
    bar._hstTimer = window.setInterval(function () {
      showPromo(bar, bar._hstIdx + 1);
    }, PROMO_MS);
  }

  function initPromo(root) {
    var bars = scoped(root, '.promo');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (!claim(bar, 'data-hst-promo')) continue;

      var items = bar.querySelectorAll('.promo__item');
      if (!items.length) continue;                    /* guard: nothing to rotate */

      bar._hstItems = Array.prototype.slice.call(items);
      bar._hstIdx = 0;
      showPromo(bar, 0);

      var navs = bar.querySelectorAll('.promo__nav');
      for (var j = 0; j < navs.length; j++) {
        (function (btn, owner) {
          btn.addEventListener('click', function () {
            var step = parseInt(btn.getAttribute('data-promo'), 10);
            showPromo(owner, owner._hstIdx + (isNaN(step) ? 1 : step));
            autoPromo(owner);                          /* restart the dwell */
          });
        })(navs[j], bar);
      }

      autoPromo(bar);
    }
  }

  function stopPromo(root) {
    var bars = find(root, '.promo');
    for (var i = 0; i < bars.length; i++) window.clearInterval(bars[i]._hstTimer);
  }


  /* ------------------------------------------------- 2 — reveal on scroll */

  var REVEAL_SEL = '[data-reveal], .rv, [data-clip]';
  var revealIO = null;

  function revealNow(el) { el.classList.add('in'); }

  function getRevealIO() {
    if (revealIO || !supportsIO) return revealIO;
    revealIO = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          revealNow(entries[i].target);
          revealIO.unobserve(entries[i].target);
        }
      }
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
    return revealIO;
  }

  function initReveal(root) {
    var targets = scoped(root, REVEAL_SEL), fresh = [], i;
    for (i = 0; i < targets.length; i++) {
      if (claim(targets[i], 'data-hst-reveal')) fresh.push(targets[i]);
    }
    if (!fresh.length) return;

    if (reduce || !supportsIO) {
      for (i = 0; i < fresh.length; i++) revealNow(fresh[i]);
      return;
    }

    var io = getRevealIO();
    for (i = 0; i < fresh.length; i++) io.observe(fresh[i]);

    /* SAFETY NET — do not remove.
       `[data-clip]` starts fully clip-pathed away and `[data-reveal]` at
       opacity:0, so if the observer never fires (element already off-screen
       in a hidden tab, a section re-rendered outside the viewport by the
       theme editor, an intersection root that never scrolls) the artwork is
       invisible with no other symptom.  Force-reveal anything still hidden
       shortly after init. */
    window.setTimeout(function () {
      for (var k = 0; k < fresh.length; k++) {
        if (!fresh[k].classList.contains('in')) {
          revealNow(fresh[k]);
          io.unobserve(fresh[k]);
        }
      }
    }, 1200);
  }


  /* ------------------------------------------------------- 3 — counters */

  var COUNT_MS = 1400;

  function runCount(el, to, dec) {
    var t0 = null;
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / COUNT_MS);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (to * eased).toFixed(dec);
      if (p < 1) window.requestAnimationFrame(step);
    }
    if (window.requestAnimationFrame) window.requestAnimationFrame(step);
    else el.textContent = to.toFixed(dec);           /* guard: no rAF */
  }

  function initCounters(root) {
    var els = scoped(root, '[data-count]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!claim(el, 'data-hst-count')) continue;

      var to = parseFloat(el.getAttribute('data-count'));
      if (isNaN(to)) to = 0;
      var dec = parseInt(el.getAttribute('data-dec') || '0', 10);
      if (isNaN(dec)) dec = 0;

      if (reduce || !supportsIO) {
        el.textContent = to.toFixed(dec);            /* final value, no animation */
        continue;
      }

      (function (node, target, decimals) {
        var cio = new IntersectionObserver(function (entries) {
          for (var k = 0; k < entries.length; k++) {
            if (entries[k].isIntersecting) {
              runCount(node, target, decimals);
              cio.unobserve(entries[k].target);
            }
          }
        }, { threshold: 0.4 });
        cio.observe(node);
        node._hstCountIO = cio;
      })(el, to, dec);
    }
  }


  /* ----------------------------------------------------- 4 — read more/less
     Expands copy that CSS has truncated with `-webkit-line-clamp`.

     MARKUP CONTRACT (any section can opt in — nothing here names a section):
       · the copy carries an id and the clamp CSS
       · a sibling <button data-hst-clamp-toggle aria-controls="<that id>"
         data-more="…" data-less="…" hidden>
       · optionally an ancestor [data-hst-clamp-host] — a fixed-height,
         overflow:hidden box that has to be allowed to grow, or the text we
         just revealed is clipped straight back off

     THE BUTTON SHIPS `hidden` AND IS ONLY REVEALED ONCE THE COPY IS PROVEN TO
     OVERFLOW. That is the whole reason this is JS and not a checkbox hack:
     a "Read more" on copy that is already fully visible is a dummy control
     (README §5.3), and CSS cannot ask whether a line clamp actually bit.
     With JS off the button stays hidden and the copy stays clamped — exactly
     today's behaviour, nothing broken, nothing lying.

     Re-measured on resize and again once webfonts land, because both change
     how many lines the copy wraps to. An OPEN control is never re-measured:
     with the clamp lifted it can no longer overflow, so measuring would hide
     the button the reader needs to collapse it again. */

  var clamps = [];
  var clampBound = false;
  var clampTimer = null;

  function clampOverflows(copy) {
    /* sub-pixel line heights make an exact > comparison flap; 1px of slack */
    return copy.scrollHeight - copy.clientHeight > 1;
  }

  /* `aria-controls` is a space-separated ID LIST in ARIA, and that is load
     bearing here rather than pedantry: a section can clamp several sibling
     paragraphs (each `-webkit-box` clamps on its own, so a two-paragraph copy
     block is two independently truncated boxes). One control has to expand all
     of them, and it must reveal itself when ANY of them is truncated. */
  function clampAnyOverflows(entry) {
    for (var i = 0; i < entry.copies.length; i++) {
      var el = entry.copies[i];
      if (clampOverflows(el)) return true;
      /* A richtext setting renders as <div class="copy"><p>…</p></div>, so the
         clamp CANNOT sit on the wrapper: a -webkit-box containing a BLOCK child
         counts box children, not text lines, and truncates after one "line"
         with a stray extra line rendered underneath. The clamp therefore lives
         on the paragraphs, and the overflow test has to follow it down there —
         measuring only the wrapper reports no overflow and the Read more
         control never appears. */
      for (var j = 0; j < el.children.length; j++) {
        if (clampOverflows(el.children[j])) return true;
      }
    }
    return false;
  }

  function syncClamp(entry) {
    var live = false, i;
    for (i = 0; i < entry.copies.length; i++) {
      if (entry.copies[i].isConnected) { live = true; break; }
    }
    if (!live) return false;                      /* editor removed them */
    if (!entry.open) entry.btn.hidden = !clampAnyOverflows(entry);
    return true;
  }

  function syncClamps() {
    for (var i = clamps.length - 1; i >= 0; i--) {
      if (!syncClamp(clamps[i])) clamps.splice(i, 1);
    }
  }
  HST.syncClamps = syncClamps;

  function onClampResize() {
    if (clampTimer) window.clearTimeout(clampTimer);
    clampTimer = window.setTimeout(syncClamps, 150);
  }

  function setClampState(entry, open) {
    entry.open = open;
    for (var i = 0; i < entry.copies.length; i++) {
      entry.copies[i].classList.toggle('is-clamp-open', open);
    }
    if (entry.host) entry.host.classList.toggle('is-clamp-open', open);
    entry.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    var label = entry.btn.getAttribute(open ? 'data-less' : 'data-more');
    if (label) entry.btn.textContent = label;
  }

  function onClampClick(e) {
    var entry = e.currentTarget._hstClamp;
    if (!entry) return;
    setClampState(entry, !entry.open);
    /* Collapsing can only ever re-truncate, so re-check the control then. */
    if (!entry.open) syncClamp(entry);
  }

  function initClamp(root) {
    var found = scoped(root, '[data-hst-clamp-toggle]'), i;
    for (i = 0; i < found.length; i++) {
      var btn = found[i];
      var refs = (btn.getAttribute('aria-controls') || '').split(/\s+/);
      var copies = [], j;
      for (j = 0; j < refs.length; j++) {
        var el = refs[j] && document.getElementById(refs[j]);
        if (el) copies.push(el);
      }
      if (!copies.length) continue;               /* nothing to expand */
      if (!claim(btn, 'data-hst-clamp-ready')) continue;
      var entry = {
        btn: btn,
        copies: copies,
        open: false,
        host: btn.closest ? btn.closest('[data-hst-clamp-host]') : null
      };
      btn._hstClamp = entry;
      clamps.push(entry);
      btn.addEventListener('click', onClampClick);
    }
    if (!clamps.length) return;
    if (!clampBound) {
      clampBound = true;
      window.addEventListener('resize', onClampResize, { passive: true });
    }
    syncClamps();
    /* Bodoni/Cabinet Grotesk land after first paint and rewrap the copy; a
       measurement taken against the fallback face is not the truth. */
    try {
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(syncClamps, function () {});
      }
    } catch (e) { /* no CSS Font Loading API — the resize pass still covers us */ }
  }


  /* --------------------------------------------------- 5 — scrolling rails
     Auto-advance, seamless looping and prev/next controls for the horizontal
     scroll-snap rails (the UGC reels, the collection discs).

     IT DRIVES THE NATIVE SCROLLER — it does not replace it with a transform
     track, and that is the design decision here. The rails are already
     overflow-x scroll-snap rows, so keeping them means touch momentum, the
     trackpad, Home/End/arrow keys, screen-reader scrolling and the browser's
     own snapping all keep working for free, and the autoplay is ~90 lines
     instead of a 140KB carousel library that would have to re-implement all
     of it. `vendor-swiper.bundle.min.js` is available and deliberately unused.

     MARKUP CONTRACT
       [data-hst-rail]                  the scroll container
       [data-hst-rail-autoplay="2000"]  dwell in ms between advances
       [data-hst-rail-loop]             wrap seamlessly (see below)
       [data-hst-rail-set="4"]          how many children are ONE pass
       [data-hst-rail-step="page"]      advance a whole page, not one card
       [data-hst-rail-prev] / [-next]   optional controls, anywhere in the
                                        rail's [data-hst-rail-scope] ancestor

     HOW THE INFINITE LOOP WORKS: the rail renders its items TWICE, so the
     pixels at `scrollLeft` and at `scrollLeft - oneSetWidth` are identical.
     Advance normally, and the moment the scroll position passes one set,
     subtract that width with smooth scrolling switched off. Nothing moves on
     screen because the two positions show the same thing — no clone
     bookkeeping, no jump, no reflow. If the section shipped only one pass we
     clone it once (aria-hidden, untabbable) so the same trick applies.

     Autoplay stops for: reduced motion, a hidden tab, a rail scrolled out of
     view, hover, keyboard focus inside it, and any manual scroll — it resumes
     one dwell after the user stops. Everything is timer-driven off the
     scroller's own idle, so no rAF loop runs while it is parked. */

  var rails = [];
  var railsBound = false;
  var RAIL_IDLE = 140;               /* ms of scroll silence == "settled" */

  function railChildren(rail) {
    var out = [], n = rail.children, i;
    for (i = 0; i < n.length; i++) if (n[i].nodeType === 1) out.push(n[i]);
    return out;
  }

  /* One card plus one gap, measured rather than assumed — the gap is a
     merchant setting and the card width is a percentage on desktop.

     `data-hst-rail-step="page"` advances a WHOLE VISIBLE PAGE instead, and it
     is deliberately computed as `cardsInView x oneCard` rather than as
     `clientWidth`. Those differ whenever the rail carries padding or the last
     card is fractionally visible, and using the raw width would leave the rail
     parked a few pixels into a card — every subsequent page then drifts
     further out of register until the cards no longer line up with the edges
     at all. Rounding to whole cards keeps every page flush forever. */
  function railStep(entry) {
    var kids = railChildren(entry.rail);
    if (kids.length < 2) return entry.rail.clientWidth || 0;
    var one = Math.round(kids[1].offsetLeft - kids[0].offsetLeft);
    if (one <= 0) return kids[0].offsetWidth || entry.rail.clientWidth || 0;
    if (entry.stepMode !== 'page') return one;
    var perPage = Math.max(1, Math.round(entry.rail.clientWidth / one));
    return one * perPage;
  }

  function railSetWidth(entry) {
    if (!entry.loop) return 0;
    var kids = railChildren(entry.rail);
    var n = entry.setCount;
    if (!(n > 0) || kids.length <= n) return 0;
    return Math.round(kids[n].offsetLeft - kids[0].offsetLeft);
  }

  /* Give a single-pass rail the second pass its loop needs. */
  function railEnsureLoop(entry) {
    if (!entry.loop) return;
    var kids = railChildren(entry.rail);
    if (!kids.length) return;
    if (entry.setCount > 0 && kids.length > entry.setCount) return;  /* already doubled */
    entry.setCount = kids.length;
    for (var i = 0; i < kids.length; i++) {
      var c = kids[i].cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-hst-rail-clone', '1');
      /* nothing inside a clone may take focus or be announced twice */
      var f = c.querySelectorAll('a,button,input,select,textarea,video,[tabindex]');
      for (var j = 0; j < f.length; j++) {
        f[j].setAttribute('tabindex', '-1');
        f[j].removeAttribute('id');
      }
      entry.rail.appendChild(c);
    }
  }

  function railMeasure(entry) {
    entry.step = railStep(entry);
    entry.setW = railSetWidth(entry);
    entry.overflows = entry.rail.scrollWidth - entry.rail.clientWidth > 1;
  }

  function railSyncControls(entry) {
    if (!entry.prev && !entry.next) return;
    var r = entry.rail;
    var max = r.scrollWidth - r.clientWidth;
    /* A control that cannot move the rail is not shown at all (§5.3). */
    if (entry.prev) entry.prev.hidden = !entry.overflows;
    if (entry.next) entry.next.hidden = !entry.overflows;
    if (entry.loop) return;                        /* looping never ends */
    if (entry.prev) entry.prev.disabled = r.scrollLeft <= 1;
    if (entry.next) entry.next.disabled = r.scrollLeft >= max - 1;
  }

  function railWrap(entry) {
    if (!entry.loop || entry.setW <= 0) return;
    var r = entry.rail;
    if (r.scrollLeft < entry.setW - 0.5) return;
    var prevBehavior = r.style.scrollBehavior;
    r.style.scrollBehavior = 'auto';               /* the jump must be instant */
    r.scrollLeft = r.scrollLeft - entry.setW;
    r.style.scrollBehavior = prevBehavior;
  }

  function railGo(entry, dir) {
    var r = entry.rail;
    if (!entry.step) railMeasure(entry);
    var target = r.scrollLeft + dir * entry.step;
    if (!entry.loop) {
      var max = r.scrollWidth - r.clientWidth;
      if (target > max - 1) target = 0;            /* rewind rather than stall */
      if (target < 0) target = max;
    }
    try { r.scrollTo({ left: target, behavior: 'smooth' }); }
    catch (e) { r.scrollLeft = target; }           /* no smooth support */
  }

  function railCanPlay(entry) {
    return !!(entry.delay && !reduce && entry.overflows && entry.visible &&
              !entry.hover && !entry.focus && !document.hidden);
  }

  function railSchedule(entry) {
    railStop(entry);
    if (!railCanPlay(entry)) return;
    entry.timer = window.setTimeout(function () {
      entry.timer = null;
      if (!railCanPlay(entry)) return;
      railGo(entry, 1);
    }, entry.delay);
  }

  function railStop(entry) {
    if (entry.timer) { window.clearTimeout(entry.timer); entry.timer = null; }
  }

  function railOnScroll(entry) {
    railStop(entry);
    if (entry.idle) window.clearTimeout(entry.idle);
    entry.idle = window.setTimeout(function () {
      entry.idle = null;
      railWrap(entry);
      railSyncControls(entry);
      railSchedule(entry);
    }, RAIL_IDLE);
  }

  function onRailNav(e) {
    var btn = e.currentTarget;
    var owner = btn._hstRailOwner;
    if (!owner) return;
    railGo(owner, btn.hasAttribute('data-hst-rail-prev') ? -1 : 1);
  }

  function initRail(root) {
    var found = scoped(root, '[data-hst-rail]'), i;
    for (i = 0; i < found.length; i++) {
      var rail = found[i];
      if (!claim(rail, 'data-hst-rail-ready')) continue;
      var scope = (rail.closest && rail.closest('[data-hst-rail-scope]')) || rail.parentNode;
      var entry = {
        rail: rail,
        delay: parseInt(rail.getAttribute('data-hst-rail-autoplay') || '0', 10) || 0,
        loop: rail.hasAttribute('data-hst-rail-loop'),
        setCount: parseInt(rail.getAttribute('data-hst-rail-set') || '0', 10) || 0,
        stepMode: rail.getAttribute('data-hst-rail-step') || 'card',
        prev: scope && scope.querySelector ? scope.querySelector('[data-hst-rail-prev]') : null,
        next: scope && scope.querySelector ? scope.querySelector('[data-hst-rail-next]') : null,
        step: 0, setW: 0, overflows: false, visible: true,
        hover: false, focus: false, timer: null, idle: null
      };
      rail._hstRail = entry;
      rails.push(entry);

      railEnsureLoop(entry);
      railMeasure(entry);
      railSyncControls(entry);

      rail.addEventListener('scroll', function (e) {
        var en = e.currentTarget._hstRail;
        if (en) railOnScroll(en);
      }, { passive: true });

      /* Pointer and keyboard both park it — nothing should slide out from
         under a reader or a focused control. */
      rail.addEventListener('mouseenter', function (e) {
        var en = e.currentTarget._hstRail; if (en) { en.hover = true; railStop(en); }
      });
      rail.addEventListener('mouseleave', function (e) {
        var en = e.currentTarget._hstRail; if (en) { en.hover = false; railSchedule(en); }
      });
      rail.addEventListener('focusin', function (e) {
        var en = e.currentTarget._hstRail; if (en) { en.focus = true; railStop(en); }
      });
      rail.addEventListener('focusout', function (e) {
        var en = e.currentTarget._hstRail;
        if (en) { en.focus = false; railSchedule(en); }
      });

      /* The owner is stamped ON THE BUTTON and read back from the event.
         Closing over `entry` here was a real bug, not a style preference:
         `var` is function-scoped, so every iteration of this loop shares ONE
         binding, and by the time any arrow was clicked it pointed at whichever
         rail happened to initialise LAST. On the home page that meant the
         testimonial arrows scrolled the blog rail. Reading the owner off the
         clicked element is the same pattern the scroll and hover handlers
         already use, and it cannot drift. */
      if (entry.prev) {
        entry.prev._hstRailOwner = entry;
        entry.prev.addEventListener('click', onRailNav);
      }
      if (entry.next) {
        entry.next._hstRailOwner = entry;
        entry.next.addEventListener('click', onRailNav);
      }

      /* Parked while off screen: an autoplaying rail nobody can see is pure
         battery and layout cost. */
      if (supportsIO) {
        var io2 = new IntersectionObserver(function (es) {
          for (var k = 0; k < es.length; k++) {
            var en = es[k].target._hstRail;
            if (!en) continue;
            en.visible = es[k].isIntersecting;
            if (en.visible) railSchedule(en); else railStop(en);
          }
        }, { threshold: 0.15 });
        io2.observe(rail);
        entry.io = io2;
      }

      railSchedule(entry);
    }

    if (!rails.length || railsBound) return;
    railsBound = true;
    window.addEventListener('resize', function () {
      if (railResizeTimer) window.clearTimeout(railResizeTimer);
      railResizeTimer = window.setTimeout(railRemeasureAll, 180);
    }, { passive: true });
    document.addEventListener('visibilitychange', function () {
      for (var k = 0; k < rails.length; k++) {
        if (document.hidden) railStop(rails[k]); else railSchedule(rails[k]);
      }
    });
  }

  var railResizeTimer = null;
  function railRemeasureAll() {
    railResizeTimer = null;
    for (var i = rails.length - 1; i >= 0; i--) {
      var en = rails[i];
      if (!en.rail.isConnected) { railStop(en); rails.splice(i, 1); continue; }
      railMeasure(en);
      railSyncControls(en);
      railSchedule(en);
    }
  }
  HST.remeasureRails = railRemeasureAll;


  /* ------------------------------------------------------------ lifecycle */

  /* Init (or re-init) every behaviour inside `root`.  Idempotent. */
  HST.init = function (root) {
    root = root || document;
    initHeader(root);
    initPromo(root);
    initReveal(root);
    initCounters(root);
    initClamp(root);
    initRail(root);
  };

  HST.destroy = function (root) {
    stopPromo(root || document);
  };

  function boot() {
    bootReady();
    HST.init(document);
  }

  /* `defer` normally means the document is already parsed by the time this
     runs, but the check keeps the file safe if someone drops the attribute. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme editor: sections are swapped in and out of the live DOM. */
  document.addEventListener('shopify:section:load', function (e) {
    markReady();                 /* a freshly injected hero must not stay at opacity:0 */
    HST.init(e.target || document);
    syncHeaders();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    HST.destroy(e.target || document);
  });
  /* Block/section selection in the editor can reveal previously off-screen
     content; re-running init is cheap because it is stamped. */
  document.addEventListener('shopify:section:select', function (e) {
    HST.init(e.target || document);
  });
})(window.HST);
